import React from 'react';
import { BaseTool, IToolManifest, IToolContext, IToolComponentProps } from '../../core/tools/interfaces/ITool';
import { ICommand } from '../../core/commands/ICommand';
import { ChangeNodeStyleCommand } from '../../core/commands/ChangeNodeStyleCommand';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';
import { Type, Check, Plus } from 'lucide-react';
import { useThemeMode } from '../../react-app/theme/ThemeProvider';
import { generateDarkModeColorPalette } from '../../react-app/theme/color-utils';
import { TOOL_APPLICABILITY } from '../../core/tools/config/toolApplicability';
import { useTranslation } from '../../hooks/useTranslation';

const presetColors = [
  // First row - light warm colors
  ['#F3CE49', '#F2C14B', '#E9A23B', '#CC7B2E'],
  // Second row - light cool to warm
  ['#4BA154', '#3982C2', '#4E46DC', '#D00C8B'],
  // Third row - mid warm to cool
  ['#8E3B1E', '#8C2822', '#306339', '#263FA9'],
  // Fourth row - mid cool to warm
  ['#5425AF', '#6A411C', '#C93B76', '#CA3A31'],
  // Fifth row - cool dark
  ['#214D3C', '#253985', '#302E7C', '#672171'],
  // Sixth row - gray
  ['#E3E8EF', '#97A3B6', '#4D5562', '#000000'],
];

const TextColorToolComponent: React.FC<IToolComponentProps> = ({ context, onExecute }) => {
  const { t } = useTranslation('diagram');
  const [open, setOpen] = React.useState(false);
  const initial = context.selectionStyles?.textColor || '#000000';
  const [selectedColor, setSelectedColor] = React.useState(initial);
  const [customColor, setCustomColor] = React.useState(initial);
  React.useEffect(() => {
    const next = context.selectionStyles?.textColor || '#000000';
    setSelectedColor(next);
    setCustomColor(next);
  }, [context.selectionStyles?.textColor]);
  const { mode } = useThemeMode();
  
  const selectedCount = context.selection.size;
  const isDisabled = selectedCount === 0;
  
  // Generate theme-appropriate color palette
  const colorPalette = React.useMemo(() => {
    if (mode === 'dark') {
      return generateDarkModeColorPalette(presetColors);
    }
    return presetColors;
  }, [mode]);

  const handleColorChange = (color: string) => {
    setSelectedColor(color);
    onExecute({ textColor: color });
  };

  const handleCustomColorChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const color = e.target.value;
    setCustomColor(color);
    setSelectedColor(color);
    handleColorChange(color);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid="tid.tools.textColor.trigger"
          disabled={isDisabled}
          className="relative w-8 h-8 flex items-center justify-center hover:bg-muted rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          aria-label={t('properties.textColor', 'Text color')}
        >
          <Type className="h-5 w-5 text-foreground" />
          <div 
            className="absolute bottom-1 left-1/2 -translate-x-1/2 w-4 h-0.5 rounded-full"
            style={{ backgroundColor: selectedColor }}
          />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[168px] p-3" align="end">
        {/* Label Header */}
        <div className="mb-3 pb-2 border-b border-border">
          <h3 className="text-[10px] font-semibold tracking-wide text-muted-foreground uppercase">{t('properties.textColor', 'Text Color')}</h3>
        </div>

        <div className="space-y-3">
          {/* Preset colors grid */}
          <div className="grid grid-cols-4 gap-2">
            {colorPalette.flat().map((color, index) => {
              const selected = color === selectedColor;
              return (
                <button
                  key={`${color}-${index}`}
                  type="button"
                  data-testid={`tid.tools.textColor.swatch.${color.replace('#', '').toLowerCase()}`}
                  className="relative flex h-6 w-6 items-center justify-center rounded-full transition-all duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-primary"
                  onClick={() => handleColorChange(color)}
                  aria-label={t('properties.applyColor', { color, defaultValue: 'Apply color {{color}}' })}
                  aria-pressed={selected}
                >
                  <span className="absolute inset-[1px] rounded-full" />
                  <span
                    className="relative inline-flex h-full w-full items-center justify-center rounded-full border"
                    style={{
                      backgroundColor: color,
                      borderColor: (() => {
                        try {
                          const r = parseInt(color.slice(1, 3), 16);
                          const g = parseInt(color.slice(3, 5), 16);
                          const b = parseInt(color.slice(5, 7), 16);
                          const darken = (c: number) => Math.max(c - 40, 0);
                          return `rgb(${darken(r)}, ${darken(g)}, ${darken(b)})`;
                        } catch {
                          return 'rgba(148, 163, 184, 0.45)';
                        }
                      })()
                    }}
                  >
                    {selected && (
                      <Check className="absolute right-auto bottom-auto h-3 w-3 text-white mix-blend-difference drop-shadow" />
                    )}
                  </span>
                </button>
              );
            })}
          </div>

          {/* Custom color row */}
          <div className="grid grid-cols-4 gap-2 pt-1">
            <button
              type="button"
              data-testid="tid.tools.textColor.custom"
              onClick={() => document.getElementById('text-color-picker')?.click()}
              className="relative flex h-6 w-6 items-center justify-center rounded-full border border-slate-300 bg-white/80 text-slate-400 transition hover:border-slate-400 hover:text-slate-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-primary"
              aria-label={t('properties.pickCustomColor', 'Pick custom color')}
            >
              <Plus className="h-3 w-3" />
            </button>
            <input
              id="text-color-picker"
              type="color"
              value={customColor}
              onChange={handleCustomColorChange}
              className="absolute h-px w-px opacity-0"
              tabIndex={-1}
              aria-hidden="true"
            />
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};

export class TextColorTool extends BaseTool {
  manifest: IToolManifest = {
    id: 'text-color',
    name: 'tools.textColor',
    icon: 'type',
    category: 'style',
    description: 'Change text color of selected shapes',
    version: '1.0.0',
    applicability: TOOL_APPLICABILITY.TEXT_SHAPES,
    
    requiresSelection: true,
    minSelection: 1,
    
    ui: {
      toolbar: true,
      contextMenu: true,
      propertyPanel: true,
      sidebar: false,
      floatingPanel: true,
      statusBar: false
    },
    
    capabilities: {
      modifiesShapes: true,
      createsShapes: false,
      deletesShapes: false,
      modifiesConnectors: false,
      createsConnectors: false,
      deletesConnectors: false,
      requiresUserInput: true,
      supportsMultiSelect: true,
      supportsBatchOperations: true
    }
  };

  canExecute(context: IToolContext): boolean {
    return super.canExecute(context) && context.selection.size > 0;
  }

  execute(params?: { textColor: string }): ICommand | null {
    if (!this.context || this.context.selection.size === 0 || !params?.textColor) {
      return null;
    }

    const selectedIds = Array.from(this.context.selection);
    return new ChangeNodeStyleCommand(selectedIds, 'textColor', params.textColor);
  }

  getToolbarComponent() {
    return TextColorToolComponent;
  }

  getFloatingPanelComponent() {
    return TextColorToolComponent;
  }
}
